import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { GraduationCap, Sparkles, X, Rocket, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';

// Launch: 01.05.2026 00:00 UTC
const LAUNCH_DATE = new Date(Date.UTC(2026, 4, 1, 0, 0, 0));
const STORAGE_KEY = 'kb_announcement_dismissed_v1';
const STORAGE_KEY_LAUNCHED = 'kb_announcement_launched_dismissed_v1';

const calcRemaining = () => {
  const now = new Date();
  const diff = LAUNCH_DATE.getTime() - now.getTime();
  if (diff <= 0) return null;
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((diff / (1000 * 60)) % 60);
  const seconds = Math.floor((diff / 1000) % 60);
  return { days, hours, minutes, seconds };
};

const Block = ({ value, label }) => (
  <div className="flex flex-col items-center">
    <div
      className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl bg-gradient-to-br from-emerald-500/15 to-emerald-700/10 border border-emerald-500/30 flex items-center justify-center font-heading text-2xl sm:text-3xl font-bold text-emerald-300 tabular-nums shadow-[0_0_20px_rgba(16,185,129,0.15)]"
      data-testid={`kb-countdown-${label.toLowerCase()}`}
    >
      {String(value).padStart(2, '0')}
    </div>
    <span className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1.5">{label}</span>
  </div>
);

const KnowledgeBaseAnnouncement = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [show, setShow] = useState(false);
  const [remaining, setRemaining] = useState(calcRemaining());
  const [launched, setLaunched] = useState(remaining === null);

  useEffect(() => {
    if (loading || !user) return;
    const isLaunched = calcRemaining() === null;
    const key = isLaunched ? STORAGE_KEY_LAUNCHED : STORAGE_KEY;
    const dismissed = localStorage.getItem(key);
    if (dismissed) return;
    const timer = setTimeout(() => setShow(true), 1500);
    return () => clearTimeout(timer);
  }, [loading, user]);

  useEffect(() => {
    const interval = setInterval(() => {
      const next = calcRemaining();
      setRemaining(next);
      if (next === null) setLaunched(true);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const dismiss = () => {
    const key = launched ? STORAGE_KEY_LAUNCHED : STORAGE_KEY;
    localStorage.setItem(key, new Date().toISOString());
    setShow(false);
  };

  const handleCta = () => {
    dismiss();
    navigate('/wissensdatenbank');
  };

  if (!user || loading) return null;

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          onClick={dismiss}
          data-testid="kb-announcement-overlay"
        >
          <motion.div
            initial={{ scale: 0.9, y: 30, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ type: 'spring', damping: 22, stiffness: 260 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-xl rounded-2xl overflow-hidden border border-emerald-500/25 bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950 shadow-[0_20px_80px_-10px_rgba(16,185,129,0.35)]"
            data-testid="kb-announcement-modal"
          >
            {/* Decorative glow */}
            <div className="pointer-events-none absolute -top-24 -right-24 w-64 h-64 rounded-full bg-emerald-500/20 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-24 -left-24 w-64 h-64 rounded-full bg-emerald-500/10 blur-3xl" />

            <button
              onClick={dismiss}
              aria-label="Schließen"
              data-testid="kb-announcement-close"
              className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center text-zinc-500 hover:text-zinc-200 hover:bg-white/5 transition z-10"
            >
              <X className="h-4 w-4" />
            </button>

            <div className="relative p-6 sm:p-8">
              <div className="flex items-center gap-2 mb-4">
                <div className="px-2.5 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center gap-1.5">
                  <Sparkles className="h-3 w-3 text-emerald-400" />
                  <span className="text-[11px] font-semibold text-emerald-300 tracking-wide uppercase">
                    {launched ? 'Jetzt Live' : 'Coming Soon'}
                  </span>
                </div>
                <span className="text-xs text-zinc-500">Premium Feature</span>
              </div>

              <div className="flex items-start gap-4 mb-5">
                <div className="shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500/25 to-emerald-700/20 border border-emerald-500/30 flex items-center justify-center">
                  {launched ? (
                    <Rocket className="h-6 w-6 text-emerald-300" />
                  ) : (
                    <GraduationCap className="h-6 w-6 text-emerald-300" />
                  )}
                </div>
                <div>
                  <h2 className="text-xl sm:text-2xl font-heading font-bold text-white leading-tight">
                    {launched ? 'Die KI-Wissensdatenbank ist live!' : 'Bald: Deine KI-Wissensdatenbank'}
                  </h2>
                  <p className="text-sm text-zinc-400 mt-1.5 leading-relaxed">
                    {launched
                      ? 'Stell deinem persönlichen KI-Trading-Mentor jede Frage und durchsuche hunderte kuratierte Kapitel zu Smart Money, ICT-Konzepten, Risikomanagement & mehr.'
                      : 'Ein interaktiver KI-Mentor mit umfassender Wissensbibliothek zu Smart Money, ICT, Risikomanagement und Psychologie. Exklusiv für Premium-Mitglieder.'}
                  </p>
                </div>
              </div>

              {!launched && remaining && (
                <div className="grid grid-cols-4 gap-2 sm:gap-3 mb-6 p-4 rounded-xl bg-black/40 border border-white/5">
                  <Block value={remaining.days} label="Tage" />
                  <Block value={remaining.hours} label="Std" />
                  <Block value={remaining.minutes} label="Min" />
                  <Block value={remaining.seconds} label="Sek" />
                </div>
              )}

              {!launched && (
                <p className="text-xs text-zinc-500 text-center mb-4">
                  Launch am{' '}
                  <span className="text-emerald-400 font-medium">01.05.2026</span>
                </p>
              )}

              <div className="flex flex-col sm:flex-row gap-2.5">
                <Button
                  data-testid="kb-announcement-cta"
                  onClick={handleCta}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold"
                >
                  {launched ? 'Jetzt entdecken' : 'Vorschau ansehen'}
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
                <Button
                  data-testid="kb-announcement-dismiss"
                  variant="outline"
                  onClick={dismiss}
                  className="sm:w-auto border-white/10 text-zinc-400 hover:text-white hover:bg-white/5"
                >
                  Später
                </Button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default KnowledgeBaseAnnouncement;
